// src/pages/ProfitLossReport/hooks/useProfitLossData.js
import { useSelector } from "react-redux";

const fmt = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const useProfitLossData = () => {
  const { balances = {} } = useSelector((state) => state.profitLoss || {});
  const authInfo  = useSelector((s) => s.authInfoReducer) || {};
  const currency  = authInfo?.currency ?? "";

  const b = balances;

  // salesBalance is gross of returns, so the "Less: Sales Return" row is subtracted here.
  const totalOperatingIncome =
    parseFloat(b.salesBalance || 0) -
    parseFloat(b.salesReturnBalance || 0) +
    parseFloat(b.serviceBalance || 0) -
    parseFloat(b.serviceReturnBalance || 0) +
    parseFloat(b.directIncomeBalance || 0);

  const totalCOGS =
    parseFloat(b.itemCostingAmount || 0) +
    parseFloat(b.serviceExpenseBalance || 0) +
    parseFloat(b.directExpenseBalance || 0);

  const grossProfit        = parseFloat(b.itemProfitLoss || 0);

  // Gross Profit breakdown -- every term of the backend itemProfitLoss formula
  // (getProfitLoss), so the note always adds up to the figure shown above it.
  const amt = (v) => `${currency} ${fmt(Math.abs(parseFloat(v || 0)))}`;
  const term = (sign, label, v) => {
    const n = parseFloat(v || 0) * (sign === "-" ? -1 : 1);
    const shown = n < 0 || (n === 0 && sign === "-") ? "-" : "+";
    return `${shown} ${label}: ${amt(v)}`;
  };
  const optional = (sign, label, v) => (parseFloat(v || 0) ? [term(sign, label, v)] : []);
  const grossProfitDetail = [
    `Sold: ${amt(b.itemSoldAmount)} - Costing: ${amt(b.itemCostingAmount)}` +
      ` = ${currency} ${fmt(parseFloat(b.itemSoldAmount || 0) - parseFloat(b.itemCostingAmount || 0))}`,
    ...optional("+", "Replace Profit", b.item_profit_replace),
    ...optional("-", "Replace Loss", b.item_replace_loss),
    term("-", "Reversal", b.item_profit_reversal),
    term("+", "Service Income", b.serviceBalance),
    ...optional("-", "Service Return", b.serviceReturnBalance),
    term("+", "Direct Income", b.directIncomeBalance),
    term("-", "Service Expense", b.serviceExpenseBalance),
    term("-", "Direct Expense", b.directExpenseBalance),
    ...optional("+", "Adjustment", b.adjustmentBalance),
  ].join(" | ") + ` = ${currency} ${fmt(b.itemProfitLoss)}`;
  const totalIndirectExpense = parseFloat(b.indirectExpenseBalance || 0);
  const totalIndirectIncome  = parseFloat(b.indirectIncomeBalance || 0);
  const netProfit          = parseFloat(b.netProfit || 0);

  const rows = [
    // ── Operating Income ─────────────────────────────────────────
    { key: "inc-header",   type: "sectionHeader", label: "Operating Income",                value: "" },
    { key: "sales",        type: "subRow",        label: "Sales",                           value: b.salesBalance,        routeKey: "sales" },
    { key: "salesReturn",  type: "subRow",        label: "Less: Sales Return",              value: -(b.salesReturnBalance || 0), routeKey: "salesReturn" },
    { key: "service",      type: "subRow",        label: "Service Income",                  value: b.serviceBalance,      routeKey: "service" },
    { key: "serviceReturn", type: "subRow",       label: "Less: Service Return",            value: -(b.serviceReturnBalance || 0), routeKey: "serviceReturn" },
    { key: "directIncome", type: "subRow",        label: "Direct Income",                   value: b.directIncomeBalance, routeKey: "directIncome" },
    { key: "total-inc",    type: "total",         label: "Total for Operating Income",      value: totalOperatingIncome },

    // ── Cost of Goods Sold ────────────────────────────────────────
    { key: "cogs-header",    type: "sectionHeader", label: "Cost of Goods Sold",            value: "" },
    { key: "cogs",           type: "subRow",        label: "Cost of Goods Sold",            value: b.itemCostingAmount,       routeKey: "productWiseProfit" },
    { key: "serviceExpense", type: "subRow",        label: "Service Expenses",              value: b.serviceExpenseBalance,   routeKey: "serviceExpense" },
    { key: "directExpense",  type: "subRow",        label: "Direct Expenses",               value: b.directExpenseBalance,    routeKey: "directExpense" },
    { key: "total-cogs",     type: "total",         label: "Total for Cost of Goods Sold",  value: totalCOGS },

    // ── Gross Profit ──────────────────────────────────────────────
    { key: "grossProfit", type: "profit", label: "Gross Profit", value: grossProfit },
    {
      key: "grossProfitDetail",
      type: "detail",
      label: grossProfitDetail,
      value: "",
    },

    // ── Non Operating Expense ─────────────────────────────────────
    { key: "exp-header",      type: "sectionHeader", label: "Non Operating Expense",              value: "" },
    { key: "indirectExpense", type: "subRow",        label: "Indirect Expenses",                  value: b.indirectExpenseBalance, routeKey: "indirectExpenses" },
    { key: "total-exp",       type: "total",         label: "Total for Non Operating Expense",    value: totalIndirectExpense },

    // ── Non Operating Income ──────────────────────────────────────
    { key: "otherinc-header", type: "sectionHeader", label: "Non Operating Income",              value: "" },
    { key: "indirectIncome",  type: "subRow",        label: "Indirect Income",                   value: b.indirectIncomeBalance, routeKey: "indirectIncome" },
    { key: "total-otherinc",  type: "total",         label: "Total for Non Operating Income",    value: totalIndirectIncome },

    // ── Net Profit ────────────────────────────────────────────────
    { key: "netProfit", type: "profit", label: "Net Profit / Loss", value: netProfit },
  ];

  return { rows, currency, balances };
};